import { creating, exist, Id } from '@campaign-portal/namespace/common/id';
import {
	AuthStrategy,
	LinkDomainStatus,
	SecureConnectionType,
	UserNotificationType,
} from '@campaign-portal/namespace/common/enums';
import { LinkDomain } from '@campaign-portal/namespace/entities/link-domains/specs';
import { SSLSettings } from '@campaign-portal/namespace/entities/portals/specs';
import { HlrConfig } from '@campaign-portal/namespace/entities/hlr-config/specs';
import { MailServerSfa } from '@campaign-portal/namespace/entities/mail-servers/specs';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { MILLISECONDS_IN_DAY } from '@campaign-portal/components-library';

const DEFAULT_HTTP_PORT = 80;
const DEFAULT_HTTPS_PORT = 443;
const SSL_WARN_PERIOD_IN_DAYS = 31;

const EMPTY_FILE: FileInfo = {
	id: '',
	name: '',
	size: 0,
};

export enum SslDomainStatus {
	ACTIVE = 'ACTIVE',
	EXPIRED = 'EXPIRED',
	WARN = 'WARN',
	EMPTY = 'EMPTY',
}

export interface LinkDomainEntityConfig<ID = exist | creating> extends Partial<LinkDomain<ID>> {
	id: ID;
}

export class LinkDomainEntity<ID = exist | creating> implements LinkDomain<ID> {
	id: ID;
	host: string;
	status: LinkDomainStatus;
	editable: boolean;
	sslSettings: SSLSettings;
	sslExpirationDate?: string;
	archivedDate?: string;
	shortLinksCount?: number;

	constructor(config: LinkDomainEntityConfig<ID>) {
		this.id = config.id;
		this.host = config.host ?? '';
		this.status = config.status ?? LinkDomainStatus.ACTIVE;
		this.editable = config.editable ?? true;
		this.sslSettings = config.sslSettings ?? {
			certFile: EMPTY_FILE,
			privateKeyFile: EMPTY_FILE,
		};
		this.sslExpirationDate = config.sslExpirationDate;
		this.archivedDate = config.archivedDate;
		this.shortLinksCount = config.shortLinksCount;
	}

	isActive(): boolean {
		return this.status === LinkDomainStatus.ACTIVE;
	}

	getSslStatus(): SslDomainStatus {
		if (!this.sslExpirationDate) {
			return SslDomainStatus.EMPTY;
		}
		const daysUntilExpiration = (new Date(this.sslExpirationDate).getTime() - Date.now()) / MILLISECONDS_IN_DAY;

		if (daysUntilExpiration < 0) {
			return SslDomainStatus.EXPIRED;
		}
		if (daysUntilExpiration < SSL_WARN_PERIOD_IN_DAYS) {
			return SslDomainStatus.WARN;
		}
		return SslDomainStatus.ACTIVE;
	}
}

export type HlrConfigEntityConfig = Partial<HlrConfig>;

export class HlrConfigEntity implements HlrConfig {
	host: string;
	port: number;
	login: string;
	password: string; // NOSONAR not a hardcoded credential

	constructor(config: HlrConfigEntityConfig = {}) {
		this.host = config.host ?? '';
		this.port = config.port ?? DEFAULT_HTTP_PORT;
		this.login = config.login ?? '';
		this.password = config.password ?? '';
	}
}

export interface MailServerSfaEntityConfig<ID = exist | creating> extends Omit<Partial<MailServerSfa<ID>>, 'ccId'> {
	id: ID;
	ccId?: Id<exist> | null;
}

export class MailServerSfaEntity<ID = exist | creating> implements Omit<MailServerSfa<ID>, 'ccId'> {
	id: ID;
	ccId: Id<exist> | null;
	name: string;
	enabled: boolean;
	notificationTypes: UserNotificationType[];
	fromName: string;
	emailAddress: string;
	replyTo: string[];
	serverIP: string;
	port: number;
	authStrategy: AuthStrategy.SFA;
	secureConnectionType: SecureConnectionType;
	login: string;
	password: string; // NOSONAR not a hardcoded credential

	creationDate?: string | null;
	createdBy?: string | null;
	lastUpdated?: string | null;
	modifiedBy?: string | null;

	constructor(config: MailServerSfaEntityConfig<ID>) {
		this.id = config.id;
		this.ccId = config.ccId ?? null;
		this.name = config.name ?? '';
		this.enabled = config.enabled ?? true;
		this.notificationTypes = config.notificationTypes ?? Object.values(UserNotificationType);
		this.fromName = config.fromName ?? '';
		this.emailAddress = config.emailAddress ?? '';
		this.replyTo = config.replyTo ?? [];
		this.serverIP = config.serverIP ?? '';
		this.port = config.port ?? DEFAULT_HTTPS_PORT;
		this.authStrategy = config.authStrategy ?? AuthStrategy.SFA;
		this.secureConnectionType = config.secureConnectionType ?? SecureConnectionType.NO_ENCRYPTION;
		this.login = config.login ?? '';
		this.password = config.password ?? '';
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}
}
